// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/orders — Order management console, after the "Orders Dispatch & Fulfillment" mockup)
// The overview of every order customers have placed: KPI cards (counts + order value), search (order id, name, phone, pincode), status pills with
// counts, an area filter, a paged table (customer, address, placed-at, status + how it was paid, items + total) and per-row actions. Editors and
// admins move an order one step forward (Confirm → Prepare → Out for delivery → Delivered) or cancel it (confirmation first; the customer is refunded
// through the normal backend path); several orders at the same step can be advanced together (one backend call each). "Export CSV" downloads the
// filtered list. Viewers get the same screen read-only. The list is the newest 100 orders (the backend cap) and refreshes itself every 30 s.
// Coming soon (dimmed, sample-free): delivery-slot and telemetry filters, cold-chain SLA card, assign captain, cold-pack manifests, audit log.
// Omitted (nothing backs them): customer ids, delivery slots, reefer temperature/pod ids, Express/VIP tags, GMV trend %, hub name/node ids.
// Neighbours: pages/admin/AdminOrderDetail (row link), utils/adminApi, components/admin/AdminParts.
// ─────────────────────────────────────────────────────────────────────────────
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronLeft, ChevronRight, CircleAlert, Download, Eye, Loader2, RotateCcw, Search, X } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { ApiError } from "../../utils/api";
import { NEXT, fetchAdminOrders, setOrderStatus } from "../../utils/adminApi";
import { STATUS_LABEL, formatWhen, paidVia, timeAgo, type Order, type OrderStatus } from "../../utils/orders";
import { downloadCsv } from "../../utils/csv";
import { formatInr } from "../../utils/priceFormat";
import { STATUS_PILL } from "../../components/admin/adminStyles";

const ALL_STATUSES: OrderStatus[] = ["placed", "confirmed", "preparing", "out_for_delivery", "delivered", "cancelled"];
const VERB: Partial<Record<OrderStatus, string>> = { confirmed: "Confirm", preparing: "Start preparing", out_for_delivery: "Send out for delivery", delivered: "Mark delivered" };
const PILLS: { id: "all" | OrderStatus; label: string }[] = [
  { id: "all", label: "All" },
  { id: "placed", label: "Awaiting" },
  { id: "confirmed", label: "Confirmed" },
  { id: "preparing", label: "Preparing" },
  { id: "out_for_delivery", label: "Out for delivery" },
  { id: "delivered", label: "Delivered" },
  { id: "cancelled", label: "Cancelled" },
];
const PAGE_SIZE = 10;
const REFRESH_MS = 30_000;
const soonChip = "cursor-not-allowed opacity-60";

const areaOf = (order: Order) => `${order.delivery.city} ${order.delivery.pincode}`;
const itemsSummary = (order: Order) => order.items.map((line) => `${line.qty} × ${line.pack === "1kg" ? "1 kg" : "500 g"} ${line.name}`).join(", ");

function exportCsv(orders: Order[]) {
  const header = ["Order", "Status", "Customer", "Phone", "Address", "City", "Pincode", "Placed at", "Items", "Paid", "Total (INR)"];
  downloadCsv("zenfresh-orders", [header, ...orders.map((o) => [o.ref, STATUS_LABEL[o.status], o.delivery.name, o.delivery.phone, o.delivery.addressLine, o.delivery.city, o.delivery.pincode, o.createdAt, itemsSummary(o), paidVia(o), o.total])]);
}

function Kpi({ label, value, hint, tone }: { label: string; value: string | number; hint: string; tone?: "urgent" }) {
  return (
    <div className={`rounded-xl p-3 shadow-sm ring-1 ring-ocean-900/5 dark:ring-white/10 ${tone === "urgent" && Number(value) > 0 ? "bg-coral-500/10" : "bg-white dark:bg-ocean-900"}`}>
      <p className="text-xs font-semibold text-ocean-800/80 dark:text-foam-100/75">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold" data-testid={`kpi-${label.toLowerCase().replace(/\W+/g, "-")}`}>{value}</p>
      <p className="text-xs text-ocean-800/75 dark:text-foam-100/70">{hint}</p>
    </div>
  );
}

export default function AdminOrders() {
  const auth = useAuth();
  const canWrite = auth.user?.role === "admin" || auth.user?.role === "editor";
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [status, setStatus] = useState<"all" | OrderStatus>("all");
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("");
  const [page, setPage] = useState(1);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setOrders(await fetchAdminOrders(ALL_STATUSES));
      setFailed(false);
    } catch {
      setFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
    // Quiet refresh: only while the tab is visible, so a background tab doesn't keep calling the API.
    const timer = window.setInterval(() => { if (document.visibilityState === "visible") void load(); }, REFRESH_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(ALL_STATUSES.map((s) => [s, 0])) as Record<OrderStatus, number>;
    let value = 0;
    for (const order of orders ?? []) {
      byStatus[order.status] += 1;
      if (order.status !== "cancelled") value += order.total;
    }
    return { byStatus, value };
  }, [orders]);

  const areas = useMemo(() => [...new Set((orders ?? []).map(areaOf))].sort(), [orders]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const digits = needle.replace(/\D/g, "");
    return (orders ?? []).filter((order) => {
      if (status !== "all" && order.status !== status) return false;
      if (area && areaOf(order) !== area) return false;
      if (!needle) return true;
      return (
        order.ref.toLowerCase().includes(needle) ||
        order.delivery.name.toLowerCase().includes(needle) ||
        order.delivery.pincode.includes(needle) ||
        (digits.length >= 3 && order.delivery.phone.replace(/\D/g, "").includes(digits))
      );
    });
  }, [orders, status, area, query]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const pickedOrders = (orders ?? []).filter((o) => picked.has(o.ref));
  const bulkNext = pickedOrders.length > 0 && pickedOrders.every((o) => NEXT[o.status] && NEXT[o.status] === NEXT[pickedOrders[0].status]) ? NEXT[pickedOrders[0].status] : undefined;

  const reset = () => { setStatus("all"); setQuery(""); setArea(""); setPage(1); };
  const toggle = (ref: string) => setPicked((prev) => { const next = new Set(prev); if (next.has(ref)) next.delete(ref); else next.add(ref); return next; });
  const allVisiblePicked = visible.length > 0 && visible.every((o) => picked.has(o.ref));

  const move = async (order: Order, next: OrderStatus) => {
    setBusy(order.ref);
    setError(null);
    setNotice("");
    try {
      await setOrderStatus(order.ref, next);
      setConfirmCancel(null);
      setNotice(`Order ${order.ref} is now ${STATUS_LABEL[next].toLowerCase()}.`);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't change that order.");
    } finally {
      setBusy(null);
    }
  };

  const moveMany = async () => {
    if (!bulkNext) return;
    setBusy("bulk");
    setError(null);
    setNotice("");
    let done = 0;
    const failures: string[] = [];
    for (const order of pickedOrders) {
      try { await setOrderStatus(order.ref, bulkNext); done += 1; } catch { failures.push(order.ref); }
    }
    setPicked(new Set(failures));
    if (failures.length) setError(`${failures.length} of ${pickedOrders.length} couldn't be changed (${failures.join(", ")}) — they're still selected.`);
    setNotice(`${done} ${done === 1 ? "order" : "orders"} moved to ${STATUS_LABEL[bulkNext].toLowerCase()}.`);
    await load();
    setBusy(null);
  };

  return (
    <div className="flex flex-col gap-4 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ocean-800/80 dark:text-foam-100/75">
            <Link to="/admin" className="underline">Command Ops</Link><span aria-hidden="true">/</span>Order management
          </p>
          <h1 className="font-display text-2xl font-bold">Orders console</h1>
          <p className="text-sm text-ocean-800/85 dark:text-foam-100/80">Every order customers have placed — confirm, prepare and dispatch from one place. Open an order for the full details.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={() => exportCsv(filtered)} disabled={filtered.length === 0} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold disabled:opacity-50 dark:bg-white/10"><Download className="h-4 w-4" aria-hidden="true" /> Export CSV</button>
          {["Staging manifests", "Cold-chain audit log"].map((label) => (
            <button key={label} type="button" disabled title="Coming soon" className={`min-h-11 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10 ${soonChip}`}>{label} <span className="text-[10px] uppercase text-coral-600 dark:text-coral-300">soon</span></button>
          ))}
        </div>
      </div>

      <section aria-label="Order figures" className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Total orders" value={orders?.length ?? "—"} hint="latest 100" />
        <Kpi label="Awaiting confirmation" value={counts.byStatus.placed} hint="needs a decision" tone="urgent" />
        <Kpi label="Being prepared" value={counts.byStatus.confirmed + counts.byStatus.preparing} hint="confirmed + preparing" />
        <Kpi label="Out for delivery" value={counts.byStatus.out_for_delivery} hint="on the road" />
        <Kpi label="Delivered" value={counts.byStatus.delivered} hint="in this list" />
        <div className="rounded-xl bg-white p-3 opacity-60 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10" aria-label="Cold-chain SLA (coming soon)">
          <p className="text-xs font-semibold">Cold-chain SLA</p>
          <p className="mt-1 font-display text-2xl font-bold">—</p>
          <p className="text-xs uppercase text-coral-600 dark:text-coral-300">Coming soon</p>
        </div>
      </section>
      <p className="text-sm" data-testid="order-value">Order value in this list (cancelled excluded): <strong>{formatInr(counts.value)}</strong></p>

      <section aria-label="Filters" className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:w-96">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 opacity-60" aria-hidden="true" />
            <label htmlFor="order-search" className="sr-only">Search orders</label>
            <input id="order-search" type="search" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Order id, customer, phone or pincode" className="min-h-11 w-full rounded-xl bg-ocean-700/5 pl-10 pr-3 text-sm dark:bg-white/10" />
          </div>
          <div role="group" aria-label="Order status" className="flex flex-wrap gap-1.5">
            {PILLS.map((pill) => {
              const n = pill.id === "all" ? orders?.length ?? 0 : counts.byStatus[pill.id];
              return (
                <button key={pill.id} type="button" aria-pressed={status === pill.id} onClick={() => { setStatus(pill.id); setPage(1); }} className={`min-h-11 rounded-full px-4 text-sm font-bold ${status === pill.id ? "bg-ocean-800 text-white" : "bg-ocean-700/10 dark:bg-white/10"}`}>{pill.label} ({n})</button>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex min-h-11 items-center gap-2 rounded-lg bg-ocean-700/5 px-3 text-sm dark:bg-white/10">
              <span className="font-semibold">Area</span>
              <select value={area} onChange={(e) => { setArea(e.target.value); setPage(1); }} className="bg-transparent font-bold">
                <option value="">All areas</option>
                {areas.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
            {["Delivery slot", "Telemetry"].map((label) => (
              <label key={label} className={`flex min-h-11 items-center gap-2 rounded-lg bg-ocean-700/5 px-3 text-sm dark:bg-white/10 ${soonChip}`}>
                <span className="font-semibold">{label}</span>
                <select disabled className="bg-transparent font-bold"><option>Coming soon</option></select>
              </label>
            ))}
          </div>
          <div className="flex items-center gap-3 text-sm">
            <button type="button" onClick={reset} className="inline-flex min-h-11 items-center gap-1 font-bold underline"><RotateCcw className="h-4 w-4" aria-hidden="true" /> Reset filters</button>
            <span role="status" data-testid="showing">Showing <strong>{filtered.length}</strong> of {orders?.length ?? 0}</span>
          </div>
        </div>
      </section>

      <p role="status" className="min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
      <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">{error && <CircleAlert className="h-4 w-4" aria-hidden="true" />}{error}</p>
      {failed && <p role="alert" className="rounded-xl bg-coral-500/10 p-4 font-semibold">We couldn&apos;t load orders.</p>}
      {!orders && !failed && <p role="status" className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>}
      {orders && filtered.length === 0 && <p className="rounded-2xl bg-white p-6 text-center shadow-sm dark:bg-ocean-900">{orders.length === 0 ? "No orders yet." : "No orders match these filters."}</p>}

      {visible.length > 0 && (
        <div className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-ocean-900/5 dark:bg-ocean-900 dark:ring-white/10">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[980px] border-collapse text-left text-sm">
              <caption className="sr-only">Orders, newest first</caption>
              <thead className="bg-ocean-700/5 text-xs uppercase tracking-wider dark:bg-white/5">
                <tr>
                  <th scope="col" className="w-10 py-3 pl-4"><input type="checkbox" aria-label="Select all orders on this page" checked={allVisiblePicked} onChange={() => setPicked((prev) => { const next = new Set(prev); visible.forEach((o) => (allVisiblePicked ? next.delete(o.ref) : next.add(o.ref))); return next; })} className="h-4 w-4 accent-ocean-800" /></th>
                  <th scope="col" className="px-3 py-3">Order</th>
                  <th scope="col" className="px-3 py-3">Customer</th>
                  <th scope="col" className="px-3 py-3">Location</th>
                  <th scope="col" className="px-3 py-3">Placed</th>
                  <th scope="col" className="px-3 py-3">Status</th>
                  <th scope="col" className="px-3 py-3">Items &amp; total</th>
                  <th scope="col" className="py-3 pl-3 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((order) => {
                  const next = NEXT[order.status];
                  return (
                    <Fragment key={order.ref}>
                      <tr data-testid="admin-order" className={`border-t border-ocean-900/5 align-top dark:border-white/10 ${picked.has(order.ref) ? "bg-ocean-700/5 dark:bg-white/5" : ""}`}>
                        <td className="py-3.5 pl-4"><input type="checkbox" aria-label={`Select order ${order.ref}`} checked={picked.has(order.ref)} onChange={() => toggle(order.ref)} className="h-4 w-4 accent-ocean-800" /></td>
                        <td className="px-3 py-3.5"><Link to={`/admin/orders/${order.ref}`} className="whitespace-nowrap font-display font-bold underline">#{order.ref}</Link></td>
                        <td className="px-3 py-3.5"><p className="font-bold">{order.delivery.name}</p><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{order.delivery.phone}</p></td>
                        <td className="max-w-[220px] px-3 py-3.5"><p className="truncate">{order.delivery.addressLine}</p><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{order.delivery.city} · <strong>{order.delivery.pincode}</strong></p></td>
                        <td className="px-3 py-3.5"><p className="font-semibold">{formatWhen(order.createdAt)}</p><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{timeAgo(order.createdAt)}</p></td>
                        <td className="px-3 py-3.5"><span className={`inline-block rounded-full px-2.5 py-1 text-xs font-bold ${STATUS_PILL[order.status]}`}>{STATUS_LABEL[order.status]}</span><p className="mt-1 text-xs text-ocean-800/80 dark:text-foam-100/75">Paid {paidVia(order)}</p></td>
                        <td className="max-w-[240px] px-3 py-3.5"><p className="font-bold">{formatInr(order.total)}</p><p className="truncate text-xs text-ocean-800/80 dark:text-foam-100/75" title={itemsSummary(order)}>{order.items.length} {order.items.length === 1 ? "item" : "items"} · {itemsSummary(order)}</p></td>
                        <td className="py-3.5 pl-3 pr-4">
                          <div className="flex items-center justify-end gap-1.5">
                            <Link to={`/admin/orders/${order.ref}`} title="Open order details" className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-ocean-700/10 dark:bg-white/10"><Eye className="h-4 w-4" aria-hidden="true" /><span className="sr-only">Open details of order {order.ref}</span></Link>
                            {canWrite && order.active && next && (
                              <button type="button" disabled={busy !== null} onClick={() => void move(order, next)} title={VERB[next]} aria-label={`${VERB[next]} — order ${order.ref}`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-teal-600 text-ocean-950 disabled:opacity-60">{busy === order.ref ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />}</button>
                            )}
                            {canWrite && order.active && (
                              <button type="button" onClick={() => setConfirmCancel(order.ref)} title="Cancel order" aria-label={`Cancel order ${order.ref}`} className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-coral-500/10 text-coral-700 dark:text-coral-300"><X className="h-4 w-4" aria-hidden="true" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {confirmCancel === order.ref && (
                        <tr><td colSpan={8} className="bg-coral-500/10 px-4 py-3">
                          <div role="alertdialog" aria-label={`Cancel order ${order.ref}?`} className="flex flex-wrap items-center gap-3">
                            <p className="text-sm font-semibold">Cancel order {order.ref}? The customer is refunded.</p>
                            <button type="button" disabled={busy !== null} onClick={() => void move(order, "cancelled")} className="min-h-11 rounded-full bg-coral-600 px-4 text-sm font-bold text-white disabled:opacity-60">Yes, cancel it</button>
                            <button type="button" onClick={() => setConfirmCancel(null)} className="min-h-11 rounded-full bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10">Keep it</button>
                          </div>
                        </td></tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <nav aria-label="Order pages" className="flex flex-wrap items-center justify-between gap-3 bg-ocean-700/5 px-4 py-3 text-sm dark:bg-white/5">
            <span>Showing <strong>{(current - 1) * PAGE_SIZE + 1}–{(current - 1) * PAGE_SIZE + visible.length}</strong> of <strong>{filtered.length}</strong> · refreshes every 30 s</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage(current - 1)} disabled={current <= 1} aria-label="Previous page" className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-white disabled:opacity-40 dark:bg-ocean-900"><ChevronLeft className="h-4 w-4" aria-hidden="true" /></button>
              <span aria-current="page" className="font-bold">Page {current} of {pages}</span>
              <button type="button" onClick={() => setPage(current + 1)} disabled={current >= pages} aria-label="Next page" className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-white disabled:opacity-40 dark:bg-ocean-900"><ChevronRight className="h-4 w-4" aria-hidden="true" /></button>
            </div>
          </nav>
        </div>
      )}

      {picked.size > 0 && (
        <div role="region" aria-label="Selected orders" className="fixed bottom-6 left-1/2 z-40 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-3 rounded-3xl bg-ocean-900 px-5 py-3 text-foam-50 shadow-2xl print:hidden">
          <span className="text-sm font-bold" data-testid="picked-count">{picked.size} {picked.size === 1 ? "order" : "orders"} selected</span>
          {canWrite && (
            <button type="button" disabled={!bulkNext || busy !== null} onClick={() => void moveMany()} title={bulkNext ? undefined : "Select orders that are all at the same step"} className="inline-flex min-h-11 items-center gap-2 rounded-full bg-teal-500 px-4 text-sm font-bold text-ocean-950 disabled:opacity-50">
              {busy === "bulk" && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}{bulkNext ? `${VERB[bulkNext]} (${picked.size})` : "Advance selected"}
            </button>
          )}
          {["Assign captain", "Cold-pack manifests"].map((label) => (
            <button key={label} type="button" disabled title="Coming soon" className={`min-h-11 rounded-full bg-white/15 px-4 text-sm font-bold ${soonChip}`}>{label}</button>
          ))}
          <button type="button" onClick={() => setPicked(new Set())} className="min-h-11 px-2 text-sm font-semibold underline">Clear selection</button>
        </div>
      )}
    </div>
  );
}
